import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import { Typography } from "@progress/kendo-react-common";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import FormTextField from "../../components/formFields/FormTextField";
import {
  requiredArrayValidator,
  requiredValidator,
} from "../../components/formFields/CommonValidator";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import RippleButton from "../../components/common/RippleButton";
import ShadowCard from "../../components/common/ShadowCard";
import { getAllActiveCities } from "../city/services/city.services";
import {
  createHotel,
  getAllHotelContacts,
  getAllHotelRoomCategory,
  getHotelByID,
  updateHotel,
} from "./services/hotel.services";
import { clearHotelDetails } from "./hotelSlice";
import { ICity } from "../city/cityModel";
import { findIndex } from "lodash";
import { IState } from "../state/stateModel";
import { ICountry } from "../country/countryModel";
import {
  CALENDERDAYS,
  STARRATING,
  STATUSARRAY,
} from "../../_contstants/common";
import { getAllActiveStates } from "../state/services/state.services";
import { getAllActiveCountries } from "../country/services/country.services";
import FormTextArea from "../../components/formFields/FormTextArea";
import FormCheckboxGroup from "../../components/formFields/FormCheckboxGroup";
import CreateHotelContact from "./CreateHotelContact";
import ButtonForAll from "../../components/common/ButtonForAll";
import CreateHotelRoomCategory from "./CreateHotelRoomCategory";
import CreateHotelSpecialRate from "./CreateHotelSpecialRate";

interface CityChangeWatcherProps {
  formRenderProps: FormRenderProps;
  CityList: ICity[];
}

const CityChangeWatcher: React.FC<CityChangeWatcherProps> = ({
  CityList,
  formRenderProps,
}) => {
  const cityID = formRenderProps.valueGetter("CityID");

  useEffect(() => {
    const CityID = cityID ? cityID : undefined;
    const cityIndex = findIndex(CityList, { ID: CityID });
    if (cityIndex > -1) {
      const city = CityList[cityIndex];
      formRenderProps.onChange("StateID", { value: city.StateID });
      formRenderProps.onChange("CountryID", { value: city.CountryID });
    } else {
      formRenderProps.onChange("CityID", { value: "" });
      formRenderProps.onChange("StateID", { value: "" });
      formRenderProps.onChange("CountryID", { value: "" });
    }
  }, [cityID]);
  return null;
};

const CreateHotel: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const HotelID = location.state?.HotelID;
  const gridRef = useRef<any>(null);
  const dispatch = useAppDispatch();

  const loading = useAppSelector((state) => state.hotel.loading);
  const hotelContactLoading = useAppSelector(
    (state) => state.hotel.hotelContactLoading
  );
  const hotelRoomCategoryLoading = useAppSelector(
    (state) => state.hotel.hotelRoomCategoryLoading
  );
  const hotelSpecialRateLoading = useAppSelector(
    (state) => state.hotel.hotelSpecialRateLoading
  );
  const HotelDetail = useAppSelector((state) => state.hotel.HotelDetail);
  const CityList = useAppSelector((state) => state.city.CityList);
  const StateList = useAppSelector((state) => state.state.StateList);
  const CountryList = useAppSelector((state) => state.country.CountryList);

  const [formKey, setFormKey] = useState(1);

  useEffect(() => {
    setFormKey(formKey + 1);
  }, [HotelDetail, CityList]);

  useEffect(() => {
    if (HotelID) {
      dispatch(getHotelByID(HotelID));
      dispatch(getAllHotelContacts({ HotelID: HotelID }));
      dispatch(getAllHotelRoomCategory({ HotelID: HotelID }));
    }
    return () => {
      dispatch(clearHotelDetails());
    };
  }, [HotelID]);

  useEffect(() => {
    dispatch(getAllActiveCities());
    dispatch(getAllActiveStates());
    dispatch(getAllActiveCountries());
  }, []);

  const handleSubmit = async (values: any) => {
    if (HotelID) {
      const editPayload = {
        ID: values.ID,
        HotelName: values?.HotelName ? values?.HotelName : "",
        Address: values?.Address ? values?.Address : "",
        CityID: values?.CityID ? +values?.CityID : null,
        StateID: values?.StateID ? +values?.StateID : null,
        CountryID: values?.CountryID ? +values?.CountryID : null,
        HotelCategory: values?.HotelCategory ? values?.HotelCategory : "",
        WeekDays: values?.WeekDays ? values?.WeekDays?.join(", ") : "",
        WeekEnds: values?.WeekEnds ? values?.WeekEnds?.join(", ") : "",
        IsActive: values?.IsActive === 1,
      };
      try {
        const response = await dispatch(updateHotel(editPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(clearHotelDetails());
          navigate("/hotel");
        }
      } catch (error) {
        console.error("Error in handleSubmit:", error);
        throw error;
      }
    } else {
      const insertPayload = {
        HotelName: values?.HotelName ? values?.HotelName : "",
        Address: values?.Address ? values?.Address : "",
        CityID: values?.CityID ? +values?.CityID : null,
        StateID: values?.StateID ? +values?.StateID : null,
        CountryID: values?.CountryID ? +values?.CountryID : null,
        HotelCategory: values?.HotelCategory ? values?.HotelCategory : "",
        WeekDays: values?.WeekDays ? values?.WeekDays?.join(", ") : "",
        WeekEnds: values?.WeekEnds ? values?.WeekEnds?.join(", ") : "",
        IsActive: values?.IsActive === 1,
      };
      try {
        const response = await dispatch(createHotel(insertPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          // dispatch(clearHotelDetails());
          // navigate("/hotel");
          navigate(`/hotel/edit`, {
            state: {
              HotelID: response.payload.Data?.HotelID,
            },
          });
        }
      } catch (error) {
        console.error("Error in handleSubmit:", error);
        throw error;
      }
    }
  };

  return (
    <>
      {(loading ||
        hotelContactLoading ||
        hotelRoomCategoryLoading ||
        hotelSpecialRateLoading) && <LoadingPanel gridRef={gridRef} />}
      <Form
        key={formKey}
        onSubmit={handleSubmit}
        initialValues={HotelDetail}
        render={(formRenderProps: FormRenderProps) => (
          <FormElement>
            <ShadowCard style={{ padding: 12 }}>
              <GridLayout
                style={{ marginRight: 20 }}
                gap={{ rows: 0, cols: 10 }}
                cols={[
                  { width: "33.33%" },
                  { width: "33.33%" },
                  { width: "33.33%" },
                ]}
              >
                <GridLayoutItem colSpan={3}>
                  <Typography.h4>
                    {HotelID ? "Update Hotel" : "Add Hotel"}
                  </Typography.h4>
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="HotelName"
                    label="Hotel Name"
                    placeholder="Hotel Name"
                    component={FormTextField}
                    validator={requiredValidator}
                    astrike={true}
                  />
                </GridLayoutItem>
                <GridLayoutItem colSpan={2} rowSpan={2}>
                  <Field
                    name="Address"
                    label="Address"
                    placeholder="Address"
                    component={FormTextArea}
                    astrike={true}
                    validator={requiredValidator}
                  />
                </GridLayoutItem>
                <GridLayoutItem style={{ width: "100%" }}>
                  <Field
                    name={"HotelCategory"}
                    label={"Hotel Category"}
                    placeholder="Hotel Category"
                    validator={requiredValidator}
                    component={FormSelectionField}
                    options={STARRATING?.map((rating: any) => {
                      return {
                        value: rating?.value,
                        label: rating?.label,
                      };
                    })}
                  />
                </GridLayoutItem>
                <GridLayoutItem
                  colSpan={3}
                  style={{ display: "flex", gap: 10 }}
                >
                  <Field
                    wrapperClassName="w-100"
                    id={"CityID"}
                    name={"CityID"}
                    label={"City"}
                    placeholder="i.e. Ahmedabad"
                    component={FormSelectionField}
                    astrike={true}
                    validator={requiredValidator}
                    options={CityList?.map((city: ICity) => {
                      return {
                        value: city?.ID,
                        label: city?.CityName,
                      };
                    })}
                  />
                  <Field
                    wrapperClassName="w-100"
                    id={"StateID"}
                    name={"StateID"}
                    label={"State"}
                    placeholder="i.e. Gujarat"
                    component={FormSelectionField}
                    disabled={true}
                    // validator={requiredValidator}
                    options={StateList?.map((state: IState) => {
                      return {
                        value: state?.ID,
                        label: state?.StateName,
                      };
                    })}
                  />
                  <Field
                    wrapperClassName="w-100"
                    id={"CountryID"}
                    name={"CountryID"}
                    label={"Country"}
                    placeholder="i.e. India"
                    component={FormSelectionField}
                    disabled={true}
                    // validator={requiredValidator}
                    options={CountryList?.map((country: ICountry) => {
                      return {
                        value: country?.ID,
                        label: country?.CountryName,
                      };
                    })}
                  />
                  <Field
                    wrapperClassName="w-100"
                    name="IsActive"
                    placeholder="i.e. Active"
                    label="Status"
                    astrike={true}
                    validator={requiredValidator}
                    component={FormSelectionField}
                    options={STATUSARRAY?.map((status: any) => {
                      return {
                        value: status?.value,
                        label: status?.label,
                      };
                    })}
                  />
                </GridLayoutItem>
                <GridLayoutItem colSpan={3}>
                  <Field
                    id={"WeekDays"}
                    name={"WeekDays"}
                    title={"Week Days"}
                    marginRight={50}
                    marginBeetween={8}
                    validator={requiredArrayValidator}
                    component={FormCheckboxGroup}
                    options={CALENDERDAYS}
                    disabledOptions={formRenderProps.valueGetter("WeekEnds")}
                  />
                </GridLayoutItem>
                <GridLayoutItem colSpan={3}>
                  <Field
                    id={"WeekEnds"}
                    name={"WeekEnds"}
                    title={"Week Ends"}
                    marginRight={50}
                    marginBeetween={8}
                    component={FormCheckboxGroup}
                    options={CALENDERDAYS}
                    disabledOptions={formRenderProps.valueGetter("WeekDays")}
                  />
                </GridLayoutItem>
                <GridLayoutItem
                  colSpan={3}
                  style={{
                    display: "flex",
                    justifyContent: "end",
                    alignItems: "end",
                    marginTop: 10,
                  }}
                >
                  <ButtonForAll
                    label={HotelID ? "Update" : "Save"}
                    type="submit"
                    disabled={!formRenderProps.allowSubmit || loading}
                  />

                  <RippleButton
                    type="button"
                    fillMode="outline"
                    themeColor="primary"
                    style={{ marginLeft: 4 }}
                    onClick={() => navigate("/hotel")}
                  >
                    Cancel
                  </RippleButton>
                </GridLayoutItem>
              </GridLayout>
            </ShadowCard>
            <CityChangeWatcher
              formRenderProps={formRenderProps}
              CityList={CityList}
            />
          </FormElement>
        )}
      />
      {HotelID && (
        <>
          <CreateHotelContact />
          <CreateHotelRoomCategory />
          <CreateHotelSpecialRate />
        </>
      )}
    </>
  );
};

export default CreateHotel;
